import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import {
  useRechargeBetList,
  usePopupTimings,
  useIsHomeDisplayReady,
  useActivities,
  isActivityIdValid,
  useIsActivityQualificationAutoEnabled,
} from "../../../modules/homeDisplay/hooks";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { ROUTES } from "@/types/routes";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ActivityType, TimingType } from "../../../modules/homeDisplay/types";
import type { RechargeBetActivity } from "../components/RechargeBet/RechargeBet.types";

const RECHARGE_BET_DEFAULT_PRIORITY = POPUP_CONFIGS.RECHARGE_BET.priority;

const RechargeBet = lazy(() =>
  import("../components/RechargeBet/RechargeBet").then(m => ({
    default: m.default,
  }))
);

/** 当天是否已显示过某活动的弹窗 */
const hasShownPopupToday = (userId: string, activityId: string): boolean => {
  try {
    const records = localStorage.getItem("shown_popup_records");
    if (!records) return false;
    const parsed = JSON.parse(records) as Record<
      string,
      { time: string; timestamp: number }
    >;
    const key = `recharge-bet_${userId}_${activityId}`;
    if (!parsed[key]) return false;
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return parsed[key].time === todayStr;
  } catch {
    return false;
  }
};

const markPopupShownToday = (userId: string, activityId: string): void => {
  try {
    const records = localStorage.getItem("shown_popup_records");
    const parsed = records
      ? (JSON.parse(records) as Record<
          string,
          { time: string; timestamp: number }
        >)
      : {};
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    parsed[`recharge-bet_${userId}_${activityId}`] = {
      time: todayStr,
      timestamp: Date.now(),
    };
    localStorage.setItem("shown_popup_records", JSON.stringify(parsed));
  } catch {
    // ignore
  }
};

/** 检查活动所有周期下所有档位是否已全部领取 */
const isAllClaimedInternal = (activity: RechargeBetActivity): boolean => {
  const groups = activity.rechargeBetUserGroups ?? [];
  if (groups.length === 0) return false;
  return groups.every(g => {
    const records = g.userActivityProgress?.userActivityClaimRecords;
    return (
      Array.isArray(records) &&
      records.length > 0 &&
      records.every(r => r.status === "CLAIMED")
    );
  });
};

/** 检查当天是否点击过该活动图标 */
const hasRechargeBetClickedToday = (
  userId?: string,
  activityId?: string
): boolean => {
  if (!userId || !activityId) return false;
  const today = new Date().toDateString();
  const key = `recharge-bet-clicked:${userId}:${activityId}:${today}`;
  return localStorage.getItem(key) === "true";
};

/** 标记当天已点击过该活动图标 */
const markRechargeBetClickedToday = (
  userId?: string,
  activityId?: string
): void => {
  if (!userId || !activityId) return;
  const today = new Date().toDateString();
  const key = `recharge-bet-clicked:${userId}:${activityId}:${today}`;
  localStorage.setItem(key, "true");
};

/** 获取活动所有周期可领取档位数量之和 */
const getClaimableBonusCount = (activity: RechargeBetActivity): number => {
  const groups = activity.rechargeBetUserGroups ?? [];
  return groups.reduce((sum, g) => {
    const records = g.userActivityProgress?.userActivityClaimRecords ?? [];
    return sum + records.filter(r => r.status === "PENDING").length;
  }, 0);
};

// 单个活动弹窗注册组件
const ActivityPopupRegistration: React.FC<{
  activity: RechargeBetActivity;
  priority: number;
  isConfigReady: boolean;
  isAuthenticated: boolean;
  userInfo: any;
  manualActivityRef: React.MutableRefObject<{
    activity: RechargeBetActivity;
  } | null>;
  onRegisterTrigger: (activityId: string, trigger: () => void) => void;
  activities: any;
}> = ({
  activity,
  priority,
  isConfigReady,
  isAuthenticated,
  userInfo,
  manualActivityRef,
  onRegisterTrigger,
  activities,
}) => {
  const activityId = activity.id;
  const popupId = `recharge-bet-auto-${activityId}`;

  // 与 RECHARGE_ACTIVITY 保持一致：校验该活动确实在后端返回的人群数据中存在
  const isActivityValid = useMemo(
    () => isActivityIdValid(activityId, ActivityType.RECHARGE_BET, activities),
    [activityId, activities]
  );
  const isAutoEnabled = useIsActivityQualificationAutoEnabled(
    ActivityType.RECHARGE_BET,
    activityId
  );

  const { trigger } = useQueuedPopup({
    popupId,
    priority,
    shouldShow: () =>
      isConfigReady && isAuthenticated && !!activity && isActivityValid,
    renderContent: onComplete => {
      // 弹窗渲染时立即标记已点击（无论自动还是手动触发），让 badge 消失
      window.dispatchEvent(
        new CustomEvent("rechargeBetOpened", { detail: activityId })
      );
      if (userInfo?.userId) {
        markRechargeBetClickedToday(userInfo.userId, activityId);
      }

      const handleClose = () => {
        manualActivityRef.current = null;
        onComplete();
      };
      const baseActivity =
        manualActivityRef.current?.activity?.id === activityId
          ? manualActivityRef.current.activity
          : activity;

      // 从 activities 配置中合并 activityEntryPoint（含 popupTitle/popupSubtitle/headerImageUrl）
      const entryPoint = (activities as any)?.[
        ActivityType.RECHARGE_BET
      ]?.list?.find((item: any) => item.id === activityId)?.activityEntryPoint;
      const displayActivity = entryPoint
        ? { ...baseActivity, activityEntryPoint: entryPoint }
        : baseActivity;

      return (
        <Suspense fallback={null}>
          <RechargeBet
            visible={true}
            data={displayActivity}
            onClose={handleClose}
          />
        </Suspense>
      );
    },
    showOnce: true,
    allowManualTrigger: true,
    autoTrigger: isAutoEnabled,
    dependencies: [isConfigReady, activity, isAuthenticated, isActivityValid, isAutoEnabled],
    allowedPaths: [
      ROUTES.HOME,
      ROUTES.HOME_ALT,
      "/",
      ROUTES.EVENT_CENTER,
      ROUTES.LOGIN,
      ROUTES.REGISTER,
    ],
    blockedPaths: [
      "/game",
      ROUTES.LOGIN,
      ROUTES.REGISTER,
      ROUTES.RECHARGE,
      ROUTES.WITHDRAW,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  useEffect(() => {
    onRegisterTrigger(activityId, trigger);
  }, [activityId, trigger, onRegisterTrigger]);

  return null;
};

const AllRechargeBetPopups: React.FC<{
  activities: RechargeBetActivity[];
  getPriority: (activity: RechargeBetActivity) => number;
  isConfigReady: boolean;
  isAuthenticated: boolean;
  userInfo: any;
  manualActivityRef: React.MutableRefObject<{
    activity: RechargeBetActivity;
  } | null>;
  onRegisterTrigger: (activityId: string, trigger: () => void) => void;
  activitiesConfig: any;
}> = ({
  activities,
  getPriority,
  isConfigReady,
  isAuthenticated,
  userInfo,
  manualActivityRef,
  onRegisterTrigger,
  activitiesConfig,
}) => (
  <>
    {activities.map(activity => (
      <ActivityPopupRegistration
        key={activity.id}
        activity={activity}
        priority={getPriority(activity)}
        isConfigReady={isConfigReady}
        isAuthenticated={isAuthenticated}
        userInfo={userInfo}
        manualActivityRef={manualActivityRef}
        onRegisterTrigger={onRegisterTrigger}
        activities={activitiesConfig}
      />
    ))}
  </>
);

export const useRechargeBetPopup = () => {
  const rechargeBetList = useRechargeBetList() as RechargeBetActivity[];
  const popupTimings = usePopupTimings();
  const { data: userInfo, isAuthenticated } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();
  const activities = useActivities();

  const manualActivityRef = useRef<{
    activity: RechargeBetActivity;
  } | null>(null);
  const triggersMap = useRef<Map<string, () => void>>(new Map());

  // 追踪每个活动当天是否已点击过（activityId -> boolean）
  const [clickedMap, setClickedMap] = useState<Record<string, boolean>>({});

  // 初始化和数据变化时重新检查 localStorage
  useEffect(() => {
    if (!userInfo?.userId || !rechargeBetList?.length) return;
    const map: Record<string, boolean> = {};
    rechargeBetList.forEach(a => {
      map[a.id] = hasRechargeBetClickedToday(userInfo.userId, a.id);
    });
    setClickedMap(map);
  }, [userInfo?.userId, rechargeBetList]);

  // 监听弹窗打开事件，更新对应活动的已点击状态
  useEffect(() => {
    const handleOpened = (e: Event) => {
      const activityId = (e as CustomEvent<string>).detail;
      if (activityId) {
        setClickedMap(prev => ({ ...prev, [activityId]: true }));
      }
    };
    window.addEventListener("rechargeBetOpened", handleOpened);
    return () => window.removeEventListener("rechargeBetOpened", handleOpened);
  }, []);

  const getPriority = useCallback(
    (activity: RechargeBetActivity): number => {
      const timings = popupTimings.filter(
        t => t.timingType === TimingType.ACTIVITY_QUALIFICATION
      );
      for (const timing of timings) {
        const matched = timing.activityItems?.find(
          item =>
            item.activityType === ActivityType.RECHARGE_BET &&
            item.activityId === activity.id
        );
        if (matched)
          return (matched.sort ?? RECHARGE_BET_DEFAULT_PRIORITY) * 100;
      }
      return RECHARGE_BET_DEFAULT_PRIORITY;
    },
    [popupTimings]
  );

  /** 检查某个活动在 ACTIVITY_QUALIFICATION 配置中是否启用自动弹窗 */
  const getActivityAutoEnabled = useCallback(
    (activityId: string): boolean => {
      const timings = popupTimings.filter(
        t => t.timingType === TimingType.ACTIVITY_QUALIFICATION
      );
      for (const timing of timings) {
        const matched = timing.activityItems?.find(
          item =>
            item.activityType === ActivityType.RECHARGE_BET &&
            item.activityId === activityId
        );
        if (matched) return matched.enabled !== false;
      }
      return true;
    },
    [popupTimings]
  );

  // 只注册在后台 popupTimings.ACTIVITY_QUALIFICATION
  // activityItems 中配置过的活动，未配置的不自动弹窗
  const validActivities = useMemo(() => {
    const timings = popupTimings.filter(
      t => t.timingType === TimingType.ACTIVITY_QUALIFICATION
    );
    const configuredIds = new Set<string>();
    timings.forEach(timing => {
      timing.activityItems?.forEach(item => {
        if (
          item.activityType === ActivityType.RECHARGE_BET &&
          item.activityId
        ) {
          configuredIds.add(item.activityId);
        }
      });
    });
    return (rechargeBetList || []).filter(
      b =>
        b &&
        Array.isArray(b.rechargeBetUserGroups) &&
        b.rechargeBetUserGroups.length > 0 &&
        configuredIds.has(b.rechargeBetUserGroups[0].activityId ?? b.id) &&
        !isAllClaimedInternal(b)
    );
  }, [rechargeBetList, popupTimings]);

  const handleRegisterTrigger = useCallback(
    (activityId: string, trigger: () => void) => {
      triggersMap.current.set(activityId, trigger);
    },
    []
  );

  // 自动弹出：有可领取档位时
  useEffect(() => {
    if (!isConfigReady || !isAuthenticated || !userInfo?.userId) return;
    if (!Array.isArray(validActivities) || validActivities.length === 0) return;

    for (const activity of validActivities) {
      if (getClaimableBonusCount(activity) === 0) continue;
      if (hasShownPopupToday(userInfo.userId, activity.id)) continue;
      if (!getActivityAutoEnabled(activity.id)) continue;

      markPopupShownToday(userInfo.userId, activity.id);
      manualActivityRef.current = { activity };
      const triggerFn = triggersMap.current.get(activity.id);
      if (triggerFn) {
        triggerFn();
      }
      break;
    }
  }, [isConfigReady, validActivities, isAuthenticated, userInfo?.userId, getActivityAutoEnabled]);

  /** 通过 activityId 手动触发弹窗（首页浮标点击） */
  const getTriggerByActivityId = useCallback(
    (activityId: string) => {
      const activity = rechargeBetList.find(a => a.id === activityId);
      if (!activity) return undefined;
      const hasActiveGroup = (activity.rechargeBetUserGroups ?? []).some(
        g => g.userActivityProgress?.participationStatus === "ACTIVE"
      );
      if (!hasActiveGroup) return undefined;
      return () => {
        manualActivityRef.current = { activity };
        markRechargeBetClickedToday(userInfo?.userId, activityId);
        window.dispatchEvent(
          new CustomEvent("rechargeBetOpened", { detail: activityId })
        );
        const triggerFn = triggersMap.current.get(activityId);
        if (triggerFn) triggerFn();
      };
    },
    [rechargeBetList, userInfo]
  );

  const shouldShow = useMemo(() => {
    return rechargeBetList.some(a =>
      (a.rechargeBetUserGroups ?? []).some(
        g => g.userActivityProgress?.participationStatus === "ACTIVE"
      )
    );
  }, [rechargeBetList]);

  /** 获取某个活动各周期中最小的剩余毫秒数（最紧迫的倒计时） */
  const getRemainingMsByActivityId = useCallback(
    (activityId: string): number => {
      const activity = rechargeBetList.find(a => a.id === activityId);
      const groups = activity?.rechargeBetUserGroups ?? [];
      const candidates = groups
        .map(g => Number(g.userActivityProgress?.countdownTime ?? NaN))
        .filter(n => Number.isFinite(n) && n > 0);
      return candidates.length > 0 ? Math.min(...candidates) : NaN;
    },
    [rechargeBetList]
  );

  /** 获取某个活动可领取档位数量（用于 badge count 显示） */
  const getClaimableCountByActivityId = useCallback(
    (activityId: string): number => {
      const activity = rechargeBetList.find(a => a.id === activityId);
      if (!activity) return 0;
      return getClaimableBonusCount(activity);
    },
    [rechargeBetList]
  );

  /**
   * 获取某个活动是否应显示 badge
   * 条件1：有待领取档位 → 显示数字
   * 条件2：当天未点击过 → 显示感叹号
   */
  const getShouldShowBadgeByActivityId = useCallback(
    (activityId: string): boolean => {
      const activity = rechargeBetList.find(a => a.id === activityId);
      if (!activity) return false;
      if (getClaimableBonusCount(activity) > 0) return true;
      if (!clickedMap[activityId]) return true;
      return false;
    },
    [rechargeBetList, clickedMap]
  );

  const PopupRegistrationComponent = useCallback(
    () => (
      <AllRechargeBetPopups
        activities={validActivities}
        getPriority={getPriority}
        isConfigReady={isConfigReady}
        isAuthenticated={isAuthenticated}
        userInfo={userInfo}
        manualActivityRef={manualActivityRef}
        onRegisterTrigger={handleRegisterTrigger}
        activitiesConfig={activities}
      />
    ),
    [
      validActivities,
      getPriority,
      isConfigReady,
      isAuthenticated,
      userInfo,
      handleRegisterTrigger,
      activities,
    ]
  );

  return {
    shouldShow,
    activities: rechargeBetList,
    getTriggerByActivityId,
    getRemainingMsByActivityId,
    getClaimableCountByActivityId,
    getShouldShowBadgeByActivityId,
    PopupRegistrationComponent,
  };
};
